import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, FileText, BarChart } from "lucide-react";
import { FunnelTemplates } from "@/components/FunnelBuilder/FunnelTemplates";
import { createFunnel, getFunnels, deleteFunnel } from "@/lib/db";
import { toast } from "sonner";
import type { FunnelData } from "@/components/FunnelBuilder/types";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SubmissionsTable } from "@/components/FunnelBuilder/SubmissionsTable";
import { AnalyticsDialog } from "@/components/FunnelBuilder/Analytics/AnalyticsDialog";
const Dashboard = () => {
  const navigate = useNavigate();
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [funnels, setFunnels] = useState<FunnelData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [funnelToDelete, setFunnelToDelete] = useState<string | null>(null);
  const [submissionsFunnelId, setSubmissionsFunnelId] = useState<string | null>(null);
  const [analyticsFunnelId, setAnalyticsFunnelId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const loadFunnels = async () => {
      try {
        const userFunnels = await getFunnels();
        setFunnels(userFunnels);
      } catch (error) {
        console.error("Hiba az oldalak betöltésekor:", error);
        toast.error("Nem sikerült betölteni az oldalakat.");
      } finally {
        setIsLoading(false);
      }
    };
    loadFunnels();
  }, []);
  const handleCreateFunnel = async (templateData: FunnelData) => {
    try {
      const funnelId = await createFunnel(templateData);
      toast.success("Oldaladat sikeresen létrehoztuk.");
      navigate(`/funnels/${funnelId}`, {
        state: {
          funnelData: templateData
        }
      });
    } catch (error) {
      toast.error("Valami hiba törétnt az oldal létrehozásakor.");
      console.error("Error creating funnel:", error);
    }
  };
  const handleDeleteFunnel = async () => {
    if (!funnelToDelete) return;
    try {
      await deleteFunnel(funnelToDelete);
      setFunnels(funnels.filter(funnel => funnel.id !== funnelToDelete));
      toast.success("Oldaladat sikeresen töröltük.");
    } catch (error) {
      console.error("Valami hiba törétnt az oldal törélésekor:", error);
      toast.error("Failed to delete funnel");
    } finally {
      setFunnelToDelete(null);
    }
  };
  const filteredFunnels = funnels.filter(funnel => funnel.siteName.toLowerCase().includes(searchTerm.toLowerCase()) || funnel.siteTitle.toLowerCase().includes(searchTerm.toLowerCase()));

  // Get the selected funnel's form elements
  const selectedFunnel = funnels.find(funnel => funnel.id === submissionsFunnelId);
  return <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-sm">
          <Input placeholder="Keresés az Oldalak között..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full" />
        </div>
        <Button onClick={() => setIsTemplateDialogOpen(true)} className="shrink-0 border border-transparent bg-primary hover:bg-primary/90 ">
          <Plus className="w-4 h-4 mr-1" /> Új Oldal
        </Button>
        
      </div>
      
      <FunnelTemplates isOpen={isTemplateDialogOpen} onClose={() => setIsTemplateDialogOpen(false)} onSelect={handleCreateFunnel} />
      
      {isLoading ? <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary border-r-transparent"></div>
        </div> : filteredFunnels.length === 0 ? <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Nincs még egyetlen oldalad sem.</h2>
          <p className="text-muted-foreground mb-4">
            {searchTerm ? "Egyetlen oldal sem felet meg a keresésnek." : "Miután létrehoztad őket itt fognak megjelenni."}
          </p>
          <Button onClick={() => setIsTemplateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Hozz létre egyet!
          </Button>
        </Card> : <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFunnels.map(funnel => <Card key={funnel.id} className="p-4 hover:shadow-lg transition-shadow duration-200">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold cursor-pointer hover:text-primary transition-colors" onClick={() => navigate(`/funnels/${funnel.id}`)}>
                    {funnel.siteName || "Névtelen Oldal"}
                  </h3>
                  {funnel.isPro && <Badge variant="secondary" className="bg-purple-100 text-purple-800 hover:bg-purple-200">
                      PRO
                    </Badge>}
                </div>
                <Badge variant={funnel.isPublished ? "default" : "secondary"}>
                  {funnel.isPublished ? "Publikus" : "Vázlat"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {funnel.siteTitle || "-"}
              </p>
              <div className="flex justify-between items-center">
                <div className="text-xs text-muted-foreground">
                  Frissítve: {formatDistanceToNow(new Date(funnel.updatedAt), {
              addSuffix: true
            })}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAnalyticsFunnelId(funnel.id)} className="hover:bg-slate-100">
                    <BarChart className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSubmissionsFunnelId(funnel.id)} className="hover:bg-slate-100">
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setFunnelToDelete(funnel.id)} className="hover:bg-red-50 hover:text-red-600 hover:border-red-200">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>)}
        </div>}

      <AlertDialog open={!!funnelToDelete} onOpenChange={() => setFunnelToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Biztosan törlöd?</AlertDialogTitle>
            <AlertDialogDescription>
              Törlés után nem tudjuk visszaállítani az oldalad, minden beállításod és adatod elveszik.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégsem</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFunnel} className="bg-destructive text-destructive-foreground">
              Törlés
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!submissionsFunnelId} onOpenChange={() => setSubmissionsFunnelId(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Űrlap beküldések</DialogTitle>
          </DialogHeader>
          {submissionsFunnelId && selectedFunnel && <SubmissionsTable funnelId={submissionsFunnelId} formElements={selectedFunnel.formElements} />}
        </DialogContent>
      </Dialog>

      {analyticsFunnelId && <AnalyticsDialog funnelId={analyticsFunnelId} isOpen={!!analyticsFunnelId} onOpenChange={open => !open && setAnalyticsFunnelId(null)} />}
    </div>;
};
export default Dashboard;